import { useState } from "react";
import axios from "axios";
import {
  Building2, MapPin, Mail, Phone, FileText, CheckCircle2, AlertCircle, Send
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

const TYPES = [
  { value: "hotel", label: "Hôtel" },
  { value: "chambre_hote", label: "Chambre d'hôte" },
  { value: "appartement_studio", label: "Appartement / Studio" },
];

const ISLANDS = [
  { value: "Ngazidja", label: "Ngazidja (Grande Comore)" },
  { value: "Anjouan", label: "Anjouan" },
  { value: "Moheli", label: "Mohéli" },
];

const STEPS = [
  { num: "1", title: "Vous envoyez votre demande", desc: "2 minutes, aucun document requis à cette étape." },
  { num: "2", title: "Notre équipe la valide", desc: "Réponse sous 48h ouvrées par email." },
  { num: "3", title: "Vous activez votre compte", desc: "Vous choisissez vos identifiants et accédez à votre espace." },
];

export default function RegisterHotel() {
  const [form, setForm] = useState({
    hotelName: "",
    type: "hotel",
    island: "Ngazidja",
    city: "",
    adress: "",
    email: "",
    phone: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async () => {
    setError(null);

    if (!form.hotelName.trim() || !form.city.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Merci de remplir tous les champs obligatoires (*).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Adresse email invalide.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/hotel-requests/public`, {
        ...form,
        hotelName: form.hotelName.trim(),
        city: form.city.trim(),
        adress: form.adress.trim() || form.city.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        description: form.description.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition";
  const selectClass =
    "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition appearance-none";

  return (
    <div className="min-h-screen bg-[#f9fafb] py-10 px-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs font-medium text-[var(--k-green)] uppercase tracking-widest mb-2">Devenir partenaire</p>
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Inscrivez votre établissement sur Komorotel</h1>
          <p className="text-sm text-gray-400 mt-2 max-w-xl mx-auto">
            Rejoignez la première plateforme de réservation dédiée aux Comores.
            Commission de 8% seulement, sans frais d'inscription ni abonnement.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* ── COMMENT ÇA MARCHE ── */}
          <div className="lg:col-span-1 flex flex-col gap-3">
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <p className="text-sm font-semibold text-gray-900 mb-4">Comment ça marche ?</p>
              <div className="flex flex-col gap-4">
                {STEPS.map((step) => (
                  <div key={step.num} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[var(--k-green-soft)] text-[var(--k-green-text)] flex items-center justify-center text-xs font-semibold shrink-0">
                      {step.num}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{step.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <p className="text-sm font-semibold text-gray-900 mb-2">Pourquoi Komorotel ?</p>
              <ul className="flex flex-col gap-2">
                {[
                  "Commission 8% — la plus basse du marché",
                  "Paiements et réservations en ligne",
                  "Visibilité auprès de la diaspora et des voyageurs",
                  "Back-office complet : tarifs, dispos, réservations",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-[var(--k-green)] shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── FORMULAIRE ── */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-6">

            {done ? (
              <div className="py-14 text-center flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-[var(--k-green-soft)] flex items-center justify-center">
                  <CheckCircle2 className="text-[var(--k-green)]" size={26} />
                </div>
                <p className="text-base font-semibold text-gray-900">Demande envoyée !</p>
                <p className="text-sm text-gray-400 max-w-sm">
                  Notre équipe va examiner votre demande. Vous recevrez un email
                  à <span className="font-medium text-gray-600">{form.email}</span> avec
                  le lien d'activation de votre compte une fois la demande validée.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Nom de l'établissement *</label>
                  <div className="relative">
                    <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={form.hotelName} onChange={set("hotelName")}
                      placeholder="ex. Hôtel Karthala" className={inputClass} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Type d'établissement *</label>
                    <select value={form.type} onChange={set("type")} className={selectClass}>
                      {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Île *</label>
                    <select value={form.island} onChange={set("island")} className={selectClass}>
                      {ISLANDS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Ville *</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="text" value={form.city} onChange={set("city")}
                        placeholder="ex. Moroni" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Adresse</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="text" value={form.adress} onChange={set("adress")}
                        placeholder="Quartier, rue…" className={inputClass} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Email de contact *</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="email" value={form.email} onChange={set("email")}
                        placeholder="contact@votre-hotel.km" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Téléphone *</label>
                    <div className="relative">
                      <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="tel" value={form.phone} onChange={set("phone")}
                        placeholder="+269 …" className={inputClass} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Description courte</label>
                  <div className="relative">
                    <FileText size={15} className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      value={form.description}
                      onChange={set("description")}
                      rows={4}
                      placeholder="Présentez votre établissement en quelques lignes : nombre de chambres, services, points forts…"
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition resize-none"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
                    <AlertCircle size={14} className="text-red-500 shrink-0" />
                    <p className="text-xs text-red-600">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[var(--k-green)] text-white text-sm font-medium rounded-lg hover:bg-[var(--k-green-hover)] transition disabled:opacity-50"
                >
                  <Send size={15} />
                  {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  En envoyant ce formulaire, vous acceptez d'être contacté par l'équipe Komorotel
                  au sujet de votre inscription.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
